import React from 'react';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ButtonLink, ClosingCTA, Container, Eyebrow, Section, sectionHeadingClass } from '../components/ui/Primitives';
import { StepFlow } from '../components/StepFlow';
import { FaqSection } from '../components/FaqSection';
import { Seo } from '../components/seo/Seo';
import { images, pillars, serviceCategories, steps } from '../data/site';
import { faqs, pageSeo } from '../data/seo';
import { breadcrumbSchema, faqSchema, legalServiceSchema, localBusinessSchema, organizationSchema, webPageSchema, websiteSchema } from '../utils/schema';
const headingClass = sectionHeadingClass;
const seo = pageSeo.home;
export function Home() {
  return <>
      <Seo title={seo.title} description={seo.description} path={seo.path} keywords={seo.keywords} image={images.hero} schemas={[organizationSchema, websiteSchema, legalServiceSchema, localBusinessSchema, webPageSchema({
      ...seo
    }), breadcrumbSchema([{
      label: 'Home',
      path: '/'
    }]), faqSchema(faqs)]} data-id="element-209" data-mp="3kvk5ok4kl15z" />

      {/* Hero */}
      <section aria-labelledby="hero-heading" className="border-b border-line" data-id="element-210" data-mp="2qwsgkjjt672o">
        <Container className="pb-14 pt-14 md:pb-20 md:pt-24" data-id="element-211" data-mp="2mw8jffz8j3gj">
          <Eyebrow data-id="element-212" data-mp="2hxnl5m3jmtuq">Ontario &amp; Quebec</Eyebrow>
          <h1 id="hero-heading" className="mt-7 max-w-[16ch] font-display text-[42px] font-medium leading-[0.99] tracking-tightest text-ink sm:text-[56px] md:mt-8 md:text-[82px] lg:text-[100px]" data-id="element-213" data-mp="2hzi4ivz62dwp">
            The AI-native law firm.
          </h1>
          <div className="mt-9 grid gap-8 md:mt-10 md:grid-cols-12 md:items-end" data-id="element-214" data-mp="23a2kdyu556pz">
            <p className="max-w-prose text-[16.5px] leading-relaxed text-graphite md:col-span-6 md:text-[19px]" data-id="element-215" data-mp="1z7pbj1k3ksoz">
              NexLaw combines technology, AI, and legal expertise to create a more
              modern legal experience for individuals and businesses.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap md:col-span-6 md:justify-end" data-id="element-216" data-mp="1om2ia0i6jru2">
              <ButtonLink to="/how-it-works" data-id="element-217" data-mp="1an2qijzdewto">Learn how NexLaw works</ButtonLink>
              <ButtonLink to="/contact" variant="outline" data-id="element-218" data-mp="1flmf6s5cmmpq">
                Contact us
              </ButtonLink>
            </div>
          </div>
        </Container>

        <Container className="pb-16 md:pb-24" data-id="element-219" data-mp="23bqfapjbw4bz">
          <figure className="overflow-hidden rounded-sm bg-sand" data-id="element-220" data-mp="28a1oqz5pocto">
            <img src={images.hero} alt="Lawyer standing by the window of a light-filled modern law office" width={1600} height={900} loading="eager" decoding="async" fetchPriority="high" className="aspect-[3/2] w-full object-cover sm:aspect-[16/9]" data-id="element-221" data-mp="28ce4r6rwsd43" />
          </figure>
        </Container>
      </section>

      {/* What is NexLaw */}
      <Section ariaLabel="A smarter way to access legal help" data-id="element-222" data-mp="fksfw3m7bxlb">
        <div className="grid gap-10 md:grid-cols-12 md:gap-16" data-id="element-223" data-mp="amqzjqg5ucdj">
          <div className="md:col-span-6" data-id="element-224" data-mp="amh5nrrzvl2k">
            <Eyebrow data-id="element-225" data-mp="odvxmo3vqwlr">What is NexLaw</Eyebrow>
            <h2 className={`mt-6 md:mt-7 ${headingClass}`} data-id="element-226" data-mp="odm9z76b51j8">
              A smarter way to access legal help.
            </h2>
            <p className="mt-6 max-w-prose text-[16.5px] leading-relaxed text-graphite md:mt-7 md:text-[17px]" data-id="element-227" data-mp="kewmumvgw5me">
              NexLaw is a modern law firm built around technology, structured
              workflows, and lawyer oversight. We use AI to help organize and prepare
              legal work, while lawyers provide review, judgment, and professional
              guidance.
            </p>
            <Link to="/about" className="mt-8 inline-flex items-center gap-2 border-b border-ink pb-1 text-[15px] font-medium text-ink transition-colors hover:border-navy hover:text-navy" data-id="element-228" data-mp="qw9ia9lxf0q">
              About NexLaw
              <ArrowForwardIcon sx={{
              fontSize: 16
            }} data-id="element-229" data-mp="27f6lllhwyz87" />
            </Link>
          </div>
          <figure className="md:col-span-6" data-id="element-230" data-mp="2ce13ml824jr9">
            <img src={images.team} alt="Two legal professionals reviewing documents together using modern legal technology" width={1200} height={900} loading="lazy" decoding="async" className="aspect-[4/3] w-full rounded-sm object-cover" data-id="element-231" data-mp="2cf2hexggjkco" />
          </figure>
        </div>
      </Section>

      {/* Why NexLaw */}
      <section aria-labelledby="why-heading" className="border-y border-line bg-sand/60" data-id="element-232" data-mp="302u3gsrsvbr5">
        <Container className="py-20 md:py-28 lg:py-32" data-id="element-233" data-mp="351ng6iz9iyq9">
          <div className="grid gap-6 md:grid-cols-12 md:items-end md:gap-8" data-id="element-234" data-mp="3513zf5lcmgdt">
            <h2 id="why-heading" className={`md:col-span-7 ${headingClass}`} data-id="element-235" data-mp="3a1ro3km3ul4v">
              Why NexLaw
            </h2>
            <p className="max-w-prose text-[16px] leading-relaxed text-graphite md:col-span-5 md:pb-2" data-id="element-236" data-mp="3e2v2e2oi893z">
              Four principles shape how the firm is designed and how the work gets
              done.
            </p>
          </div>
          <ul className="mt-12 grid gap-px overflow-hidden rounded-sm bg-line md:mt-14 md:grid-cols-2" data-id="element-237" data-mp="tea75ihufus8">
            {pillars.map(pillar => <li key={pillar.title} className="bg-paper p-7 sm:p-8 md:p-10" data-id="element-238" data-mp="og8m4s0uxle4">
                <h3 className="font-display text-[21px] font-medium tracking-tight text-ink md:text-[22px]" data-id="element-239" data-mp="1m3h80o4rswgn">
                  {pillar.title}
                </h3>
                <p className="mt-3 max-w-prose text-[15.5px] leading-relaxed text-graphite" data-id="element-240" data-mp="1h68lkbcla69l">
                  {pillar.body}
                </p>
              </li>)}
          </ul>
        </Container>
      </section>

      {/* How it works */}
      <Section ariaLabel="How NexLaw works" data-id="element-241" data-mp="24uibnpgu5g6j">
        <div className="grid gap-6 md:grid-cols-12 md:items-end md:gap-8" data-id="element-242" data-mp="24v9zge46y9m9">
          <div className="md:col-span-7" data-id="element-243" data-mp="201fhpg85c4pt">
            <Eyebrow data-id="element-244" data-mp="3uaug89n8bhmv">How it works</Eyebrow>
            <h2 className={`mt-6 md:mt-7 ${headingClass}`} data-id="element-245" data-mp="3ubvtmkrpwg7r">How NexLaw works.</h2>
          </div>
          <div className="md:col-span-5 md:pb-2 md:text-right" data-id="element-246" data-mp="25i6qnt4advz">
            <ButtonLink to="/how-it-works" variant="outline" data-id="element-247" data-mp="3klaveyfyhdpd">
              See the full model
            </ButtonLink>
          </div>
        </div>
        <StepFlow steps={steps} data-id="element-248" data-mp="3pi19xhc4418o" />
      </Section>

      {/* Services snapshot */}
      <section aria-labelledby="services-heading" className="border-y border-line" data-id="element-249" data-mp="3altralgcr7vi">
        <Container className="py-20 md:py-28 lg:py-32" data-id="element-250" data-mp="3fly6m85ixwk5">
          <div className="grid gap-6 md:grid-cols-12 md:items-end md:gap-8" data-id="element-251" data-mp="3fjbstqs1ikth">
            <div className="md:col-span-7" data-id="element-252" data-mp="31nri3imgt8x5">
              <Eyebrow data-id="element-253" data-mp="36khx02mj9ygz">Services</Eyebrow>
              <h2 id="services-heading" className={`mt-6 md:mt-7 ${headingClass}`} data-id="element-254" data-mp="36ljkj6kqkdfl">
                Legal services for modern needs.
              </h2>
            </div>
            <p className="max-w-prose text-[16px] leading-relaxed text-graphite md:col-span-5 md:pb-2" data-id="element-255" data-mp="14twsr2t0sei2">
              Four categories covering the legal needs individuals, families, and
              businesses meet most often.
            </p>
          </div>

          <div className="mt-12 grid gap-x-10 gap-y-10 sm:grid-cols-2 md:mt-14 lg:grid-cols-4" data-id="element-256" data-mp="19wnto5g5oxsi">
            {serviceCategories.map(category => <div key={category.id} className="border-t border-ink pt-5 md:pt-6" data-id="element-257" data-mp="1iy41q6ji0ntk">
                <h3 className="font-display text-[20px] font-medium leading-snug tracking-tight text-ink md:text-[21px]" data-id="element-258" data-mp="1xna1bvm90g1z">
                  {category.title}
                </h3>
                <ul className="mt-4 space-y-2 md:mt-5" data-id="element-259" data-mp="26ohmdbzr7ke4">
                  {category.items.slice(0, 4).map(item => <li key={item} className="text-[14.5px] text-mist" data-id="element-260" data-mp="22mwde1qcbq97">
                      {item}
                    </li>)}
                </ul>
              </div>)}
          </div>

          <div className="mt-12 md:mt-14" data-id="element-261" data-mp="8a6grc8l82y4">
            <ButtonLink to="/services" variant="outline" data-id="element-262" data-mp="8bhm3gholirc">
              All services
            </ButtonLink>
          </div>
        </Container>
      </section>

      {/* Responsible AI */}
      <Section ariaLabel="Technology with lawyer oversight" data-id="element-263" data-mp="11r1w934k8j1u">
        <div className="grid gap-10 md:grid-cols-12 md:gap-16" data-id="element-264" data-mp="16kxr3wpj1pss">
          <figure className="order-2 md:order-1 md:col-span-5" data-id="element-265" data-mp="1al88e5irs2my">
            <img src={images.abstract} alt="Abstract network of connected lines representing AI-supported legal workflows" width={1200} height={900} loading="lazy" decoding="async" className="aspect-[4/3] w-full rounded-sm object-cover md:aspect-[4/5]" data-id="element-266" data-mp="1akorms4uvkai" />
          </figure>
          <div className="order-1 md:order-2 md:col-span-7" data-id="element-267" data-mp="rwjqztjhwffx">
            <Eyebrow data-id="element-268" data-mp="1ybkdz9qjlnad">Responsible AI</Eyebrow>
            <h2 className={`mt-6 max-w-[20ch] md:mt-7 ${headingClass}`} data-id="element-269" data-mp="1yasrcoexazw8">
              Technology with lawyer oversight.
            </h2>
            <p className="mt-6 max-w-prose font-serif text-[21px] font-light leading-[1.36] text-ink md:text-[24px]" data-id="element-270" data-mp="2ddstxketlwtg">
              Technology supports the work. Lawyers stand behind it.
            </p>
            <ul className="mt-9 divide-y divide-line border-y border-line md:mt-10" data-id="element-271" data-mp="23o0y6muzixne">
              {['AI supports the process — organizing, preparing, and structuring.', 'Lawyers review the work and remain accountable for the advice.', 'Client trust and confidentiality guide every tool we adopt.', 'Innovation is paired with professional responsibility, always.'].map(point => <li key={point} className="py-5 text-[15.5px] leading-relaxed text-graphite md:text-[16px]" data-id="element-272" data-mp="1krhi1dlrk9hc">
                  {point}
                </li>)}
            </ul>
            <Link to="/how-it-works" className="mt-8 inline-flex items-center gap-2 border-b border-ink pb-1 text-[15px] font-medium text-ink transition-colors hover:border-navy hover:text-navy" data-id="element-273" data-mp="3b1b2au4kkwwx">
              How responsible AI works at NexLaw
              <ArrowForwardIcon sx={{
              fontSize: 16
            }} data-id="element-274" data-mp="mmivf640d68f" />
            </Link>
          </div>
        </div>
      </Section>

      {/* Ontario & Quebec */}
      <section aria-labelledby="jurisdiction-heading" className="border-y border-line bg-sand/60" data-id="element-275" data-mp="ho7lkvdpqvq7">
        <Container className="py-16 md:py-20" data-id="element-276" data-mp="3q1ewhg4ps1ro">
          <div className="grid gap-6 md:grid-cols-12 md:items-end md:gap-10" data-id="element-277" data-mp="3l56apjw0habj">
            <div className="md:col-span-7" data-id="element-278" data-mp="3nn360bpn1x8">
              <Eyebrow data-id="element-279" data-mp="3pho3emzeu1b">Jurisdictions</Eyebrow>
              <h2 id="jurisdiction-heading" className={`mt-6 md:mt-7 ${headingClass}`} data-id="element-280" data-mp="3v2kqrie0vx3b">
                Serving Ontario and Quebec.
              </h2>
            </div>
            <p className="max-w-prose text-[16px] leading-relaxed text-graphite md:col-span-5 md:pb-2" data-id="element-281" data-mp="37b5lbgqy8znu">
              NexLaw is initially focused on Ontario and Quebec, with additional
              jurisdictions considered over time.{' '}
              <Link to="/services" className="text-ink underline decoration-line underline-offset-4 transition-colors hover:decoration-navy" data-id="element-282" data-mp="1mmbaazk91wig">
                See what we cover
              </Link>
              .
            </p>
          </div>
        </Container>
      </section>

      {/* About */}
      <section aria-labelledby="about-heading" className="border-b border-line" data-id="element-283" data-mp="20mvp7kn1n86o">
        <Container className="py-20 md:py-28 lg:py-32" data-id="element-284" data-mp="25ke2toc0abl0">
          <h2 id="about-heading" className="sr-only" data-id="element-285" data-mp="1rlo4649jg3uj">
            About NexLaw
          </h2>
          <div className="grid gap-10 md:grid-cols-12 md:items-center md:gap-16" data-id="element-286" data-mp="1wkqzgwzpuvpb">
            <figure className="md:col-span-5" data-id="element-287" data-mp="1wmvby3bol317">
              <img src={images.portrait} alt="Portrait of a legal professional working in a quiet, daylit office" width={1000} height={1250} loading="lazy" decoding="async" className="aspect-[4/5] w-full rounded-sm object-cover" data-id="element-288" data-mp="g698d3t2fi10" />
            </figure>
            <div className="md:col-span-7" data-id="element-289" data-mp="2d4q2bna0g8ox">
              <Eyebrow data-id="element-290" data-mp="2d3na9c0yc07f">About NexLaw</Eyebrow>
              <p className="mt-6 font-serif text-[23px] font-light leading-[1.34] text-ink sm:text-[27px] md:mt-7 md:text-[34px]" data-id="element-291" data-mp="293cw3c30a3hh">
                NexLaw was created to explore a better legal model — one that combines
                the strength of legal professionals with the speed and structure of
                modern technology.
              </p>
              <div className="mt-8 md:mt-9" data-id="element-292" data-mp="1u6vnutq454z5">
                <ButtonLink to="/about" variant="outline" data-id="element-293" data-mp="1q4ihq47gf51u">
                  Read our story
                </ButtonLink>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <FaqSection data-id="element-294" data-mp="3kit66bk4zx73" />

      <ClosingCTA data-id="element-295" data-mp="3ki9yros44r7b" />
    </>;
}